// Question text, case scenarios, explanations and test instructions. Plain text only (no HTML is ever
// read): blank lines make paragraphs, lines starting with "- " make a list, a block of lines with tabs
// (a table pasted from Excel) or "|" between cells makes a table, and https links work.
import { Fragment } from 'preact';

const URL_RE = /(https:\/\/[^\s<>"']+)/g;

export function Linked({ text: raw }) {
  const text = String(raw == null ? '' : raw);
  const out = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    let end = m[0].length; // a full stop or bracket right after a link isn't part of it
    while (end > 0 && '.,;:!?)]'.includes(m[0][end - 1])) end -= 1;
    const url = m[0].slice(0, end);
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<a href={url} target="_blank" rel="noopener noreferrer" key={m.index}>{url}</a>);
    last = m.index + url.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Plain string checks and short, simple patterns: question text comes from teachers' files and can be long.
const isTableLine = (l) => {
  if (l.includes('\t')) return true;
  const t = l.trim();
  return t.length > 1 && t[0] === '|' && t[t.length - 1] === '|';
};
const isBullet = (l) => /^\s*[-•]\s/.test(l);
const isRule = (cells) => cells.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c.trim()));
const isNumber = (c) => c.length <= 40 && /^\(?[-−]?(?:[$₹£€]|Rs\.?)?[\d,]+(?:\.\d+)?%?\)?$/.test(c.replace(/\s/g, ''));

function cellsOf(line) {
  if (line.includes('\t')) return line.split('\t');
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

export function blocksOf(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    if (!lines[i].trim()) { i += 1; continue; }
    if (isTableLine(lines[i])) {
      const rows = [];
      while (i < lines.length && isTableLine(lines[i])) {
        const cells = cellsOf(lines[i]);
        if (!isRule(cells)) rows.push(cells);
        i += 1;
      }
      blocks.push({ kind: 'table', rows });
    } else if (isBullet(lines[i])) {
      const items = [];
      while (i < lines.length && isBullet(lines[i])) { items.push(lines[i].replace(/^\s*[-•]\s+/, '')); i += 1; }
      blocks.push({ kind: 'list', items });
    } else {
      const para = [];
      while (i < lines.length && lines[i].trim() && !isTableLine(lines[i]) && !isBullet(lines[i])) { para.push(lines[i]); i += 1; }
      blocks.push({ kind: 'p', lines: para });
    }
  }
  return blocks;
}

function Table({ rows }) {
  const width = rows.reduce((w, r) => Math.max(w, r.length), 0);
  const full = rows.map((r) => Array.from({ length: width }, (_, k) => (r[k] || '').trim()));
  // A first row without figures is the header (the usual shape of an accounts extract).
  const head = full.length > 1 && full[0].every((c) => !isNumber(c)) && full.slice(1).some((r) => r.some(isNumber)) ? full[0] : null;
  const body = head ? full.slice(1) : full;
  // A column of figures lines up on the right, header included.
  const numCol = Array.from({ length: width }, (_, k) => body.some((r) => r[k]) && body.every((r) => !r[k] || isNumber(r[k])));
  return (
    <div class="rich-table" tabIndex={0} role="region" aria-label="Table">
      <table>
        {head ? <thead><tr>{head.map((c, k) => <th key={k} scope="col" class={numCol[k] ? 'num' : ''}>{c}</th>)}</tr></thead> : null}
        <tbody>
          {body.map((r, j) => (
            <tr key={j}>{r.map((c, k) => <td key={k} class={numCol[k] || isNumber(c) ? 'num' : ''}><Linked text={c} /></td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Rich({ text, class: cls }) {
  const blocks = blocksOf(text);
  if (!blocks.length) return null;
  return (
    <div class={`rich${cls ? ` ${cls}` : ''}`}>
      {blocks.map((b, i) => {
        if (b.kind === 'table') return <Table rows={b.rows} key={i} />;
        if (b.kind === 'list') return <ul key={i}>{b.items.map((it, j) => <li key={j}><Linked text={it} /></li>)}</ul>;
        return <p key={i}>{b.lines.map((l, j) => <Fragment key={j}>{j ? <br /> : null}<Linked text={l} /></Fragment>)}</p>;
      })}
    </div>
  );
}

// One line of a question for lists: the text before any table, shortened.
export function firstLine(text, max = 140) {
  const line = String(text || '').split('\n').find((l) => l.trim() && !isTableLine(l)) || '';
  const t = line.trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}
